import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShieldCheck, AlertTriangle, ChevronDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { API } from "../App";
import axios from "axios";

const SecurityQuestionPrompt = ({ onComplete }) => {
  const { t } = useLanguage();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const questions = [t("sq_pet"), t("sq_city"), t("sq_teacher"), t("sq_food"), t("sq_friend")];

  const handleSave = async () => {
    if (!question || !answer.trim()) { setError(t("register_security_required")); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/security-question`, { security_question: question, security_answer: answer }, { withCredentials: true });
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="security-question-prompt">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in relative max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <h2 className="font-['Outfit'] text-lg font-semibold text-[#111111]">{t("profile_security_title")}</h2>
            <p className="text-xs text-[#6B7280]">{t("security_prompt_subtitle")}</p>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 mb-5 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">{t("register_security_warning")}</p>
        </div>

        {error && <p className="text-sm text-red-500 mb-3" data-testid="security-prompt-error">{error}</p>}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#111111] font-medium text-sm">{t("register_security_question")}</Label>
            {/* Custom dropdown instead of native select for WebView compatibility */}
            <div className="relative" data-testid="security-prompt-question">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full h-11 bg-white text-left border border-[#E5E5EA] rounded-xl px-4 text-sm flex items-center justify-between"
                style={{color: question ? '#111111' : '#6B7280'}}
              >
                <span className="truncate">{question || t("register_select_question")}</span>
                <ChevronDown className="w-4 h-4 text-[#6B7280] flex-shrink-0 ml-2" />
              </button>
              {dropdownOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E5EA] rounded-xl shadow-lg overflow-hidden" style={{zIndex: 10}}>
                  {questions.map((q, i) => (
                    <button
                      key={`sq-${i}`}
                      type="button"
                      onClick={() => { setQuestion(q); setDropdownOpen(false); }}
                      className="w-full text-left px-4 py-3 text-sm hover:bg-[#F5F5F7] border-b border-[#E5E5EA] last:border-b-0"
                      style={{color: '#111111'}}
                      data-testid={`security-question-option-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#111111] font-medium text-sm">{t("register_security_answer")}</Label>
            <Input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={t("register_security_answer_placeholder")}
              className="h-11 bg-white border-[#E5E5EA] rounded-xl"
              style={{color: '#111111', WebkitTextFillColor: '#111111'}}
              data-testid="security-prompt-answer"
            />
          </div>
          <Button onClick={handleSave} disabled={loading} className="w-full h-11 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-xl" data-testid="security-prompt-save">
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t("profile_security_save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecurityQuestionPrompt;
